import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast, Toaster } from 'sonner';
import { Save, MapPin, ShoppingBag, FileText, Loader2, Search, Zap, Eye, EyeOff, CreditCard, Truck, MessageSquare, Send, CheckCircle2, XCircle, Copy } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { storesAPI } from '../../services/api';
import api from '../../services/api';
import { ShippingRateTester } from '../../components/hq/ecom/ShippingRateTester';

const QUILL_TOOLBAR = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote'],
    [{ align: [] }, { color: [] }],
    ['clean'],
  ],
};

const StoreEcommerceSettingsPage = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [settings, setSettings] = useState(null);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [testMobile, setTestMobile] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Load stores + master state list on mount
  useEffect(() => {
    (async () => {
      try {
        const [sRes, stRes] = await Promise.all([
          storesAPI.list(),
          api.get('/ecommerce/states'),
        ]);
        setStores(sRes.data || []);
        setStates(stRes.data?.states || []);
        if ((sRes.data || []).length > 0) {
          setSelectedStoreId(sRes.data[0].id);
        }
      } catch (e) {
        toast.error('Failed to load initial data');
      }
    })();
  }, []);

  const loadSettings = useCallback(async (storeId) => {
    if (!storeId) return;
    setLoading(true);
    try {
      const res = await api.get(`/ecommerce/store-settings/${storeId}`);
      setSettings(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(selectedStoreId); }, [selectedStoreId, loadSettings]);

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));

  const toggleState = (state) => {
    const cur = new Set(settings?.serving_states || []);
    if (cur.has(state)) cur.delete(state); else cur.add(state);
    update({ serving_states: Array.from(cur) });
  };

  const selectAllVisible = () => {
    const filtered = filteredStates();
    const cur = new Set(settings?.serving_states || []);
    filtered.forEach((s) => cur.add(s));
    update({ serving_states: Array.from(cur) });
  };
  const clearAllVisible = () => {
    const filtered = new Set(filteredStates());
    const cur = (settings?.serving_states || []).filter((s) => !filtered.has(s));
    update({ serving_states: cur });
  };

  const filteredStates = () => {
    if (!stateFilter.trim()) return states;
    const q = stateFilter.toLowerCase();
    return states.filter((s) => s.toLowerCase().includes(q));
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = {
        ecommerce_enabled: settings.ecommerce_enabled,
        min_order_value: Number(settings.min_order_value) || 0,
        shipping_discount_percent: Number(settings.shipping_discount_percent) || 0,
        takeaway_enabled: settings.takeaway_enabled,
        max_items_per_order: Number(settings.max_items_per_order) || 50,
        prepaid_discount_percent: Number(settings.prepaid_discount_percent) || 0,
        free_delivery_above: settings.free_delivery_above === '' || settings.free_delivery_above == null ? null : Number(settings.free_delivery_above),
        serving_states: settings.serving_states || [],
        privacy_policy_html: settings.privacy_policy_html || '',
        terms_of_use_html: settings.terms_of_use_html || '',
        return_cancellation_policy_html: settings.return_cancellation_policy_html || '',
        // 3rd-party integrations
        cod_enabled: settings.cod_enabled !== false,
        razorpay_enabled: !!settings.razorpay_enabled,
        razorpay_key_id: settings.razorpay_key_id || '',
        razorpay_key_secret: settings.razorpay_key_secret || '',
        razorpay_webhook_secret: settings.razorpay_webhook_secret || '',
        shiprocket_enabled: !!settings.shiprocket_enabled,
        shiprocket_email: settings.shiprocket_email || '',
        shiprocket_password: settings.shiprocket_password || '',
        shiprocket_channel_id: settings.shiprocket_channel_id || '',
        shiprocket_pickup_location: settings.shiprocket_pickup_location || 'Primary',
        pickup_pincode: settings.pickup_pincode || '',
        shiprocket_webhook_token: settings.shiprocket_webhook_token || '',
        sms_provider: settings.sms_provider || 'mock',
        sms_api_key: settings.sms_api_key || '',
        sms_sender_id: settings.sms_sender_id || '',
        sms_template_id: settings.sms_template_id || '',
        otp_dev_mode: settings.otp_dev_mode !== false,
        meraotp_api_key: settings.meraotp_api_key || '',
        meraotp_length: settings.meraotp_length || 6,
      };
      const res = await api.put(`/ecommerce/store-settings/${selectedStoreId}`, payload);
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTestSms = async () => {
    if (!selectedStoreId) return;
    const cleaned = (testMobile || '').replace(/\D/g, '').slice(-10);
    if (cleaned.length !== 10) {
      toast.error('Enter a 10-digit Indian mobile number');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(
        `/ecommerce/store-settings/${selectedStoreId}/test-sms`,
        { mobile: cleaned }
      );
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('Test OTP sent — check the phone.');
      } else {
        toast.error(res.data.message || 'Send failed');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setTestResult({ success: false, message: detail, provider: settings.sms_provider });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  if (!settings) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    );
  }

  const selectedCount = (settings.serving_states || []).length;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="ecom-settings-page">
      <Toaster />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">E-Commerce Settings</h1>
          <p className="text-sm text-gray-500 mt-1">
            State-wise store mapping, checkout rules, and public policies.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="min-w-[240px]">
            <Label className="text-xs text-gray-500">Store</Label>
            <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
              <SelectTrigger data-testid="ecom-store-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {stores.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={saving || loading} data-testid="ecom-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general" className="gap-2">
            <ShoppingBag className="w-4 h-4" /> General
          </TabsTrigger>
          <TabsTrigger value="states" className="gap-2">
            <MapPin className="w-4 h-4" /> State Mapping
            {selectedCount > 0 && <Badge variant="secondary" className="ml-1">{selectedCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="terms" className="gap-2">
            <FileText className="w-4 h-4" /> E-Com Terms
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2">
            <Zap className="w-4 h-4" /> Integrations
          </TabsTrigger>
        </TabsList>

        {/* GENERAL */}
        <TabsContent value="general" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>General</CardTitle>
              <CardDescription>Enable e-commerce and basic checkout rules for this store.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-gray-800">E-Commerce Enabled</p>
                  <p className="text-xs text-gray-500">Show this store on the website & allow online orders.</p>
                </div>
                <Switch
                  checked={!!settings.ecommerce_enabled}
                  onCheckedChange={(v) => update({ ecommerce_enabled: v })}
                  data-testid="ecom-enabled-switch"
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border">
                <div>
                  <p className="font-medium text-gray-800">Takeaway Enabled</p>
                  <p className="text-xs text-gray-500">Customer can pick up the order from the store.</p>
                </div>
                <Switch
                  checked={!!settings.takeaway_enabled}
                  onCheckedChange={(v) => update({ takeaway_enabled: v })}
                  data-testid="ecom-takeaway-switch"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Min Order Value (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={settings.min_order_value}
                    onChange={(e) => update({ min_order_value: e.target.value })}
                    data-testid="ecom-min-order"
                  />
                  <p className="text-xs text-gray-500 mt-1">0 = no minimum</p>
                </div>
                <div>
                  <Label>Max Items / Order</Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={settings.max_items_per_order}
                    onChange={(e) => update({ max_items_per_order: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Shipping Discount (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.shipping_discount_percent}
                    onChange={(e) => update({ shipping_discount_percent: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Prepaid Discount (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={settings.prepaid_discount_percent}
                    onChange={(e) => update({ prepaid_discount_percent: e.target.value })}
                    data-testid="ecom-prepaid-discount"
                  />
                  <p className="text-xs text-gray-500 mt-1">Reward for online (non-COD) payments</p>
                </div>
                <div>
                  <Label>Free Delivery Above (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={settings.free_delivery_above ?? ''}
                    onChange={(e) => update({ free_delivery_above: e.target.value })}
                    placeholder="e.g. 999 — empty = off"
                    data-testid="ecom-free-delivery-above"
                  />
                  <p className="text-xs text-gray-500 mt-1">Orders at/above this value (after coupon) ship free; website shows "Add ₹Y more"</p>
                </div>
              </div>

              <div className="space-y-2" data-testid="payment-options-card">
                <Label className="text-gray-800 font-medium">Payment Options</Label>
                <p className="text-xs text-gray-500">Controls which payment methods the website offers at checkout.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium text-gray-800">Cash on Delivery (COD)</p>
                      <p className="text-xs text-gray-500">Customer pays the courier on delivery.</p>
                    </div>
                    <Switch
                      checked={settings.cod_enabled !== false}
                      onCheckedChange={(v) => update({ cod_enabled: v })}
                      data-testid="ecom-cod-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div>
                      <p className="font-medium text-gray-800">Razorpay (online payment)</p>
                      <p className="text-xs text-gray-500">
                        UPI, cards, netbanking.{' '}
                        {settings.razorpay_enabled && !(settings.razorpay_key_id && settings.razorpay_key_secret) && (
                          <span className="text-red-600 font-medium">Add Razorpay keys in the Integrations tab — it stays hidden on the website until then.</span>
                        )}
                      </p>
                    </div>
                    <Switch
                      checked={!!settings.razorpay_enabled}
                      onCheckedChange={(v) => update({ razorpay_enabled: v })}
                      data-testid="ecom-razorpay-switch"
                    />
                  </div>
                </div>
                {settings.cod_enabled === false && !settings.razorpay_enabled && (
                  <p className="text-xs text-red-600 font-medium" data-testid="payment-options-warning">Both payment methods are off — customers will not be able to place orders.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* STATE MAPPING */}
        <TabsContent value="states" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>State-wise Store Mapping</CardTitle>
              <CardDescription>
                Pick the Indian states where this store fulfils orders. The website routes
                incoming orders to the mapped store based on the customer's delivery state.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px]">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                  <Input
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    placeholder="Search states"
                    className="pl-8"
                    data-testid="ecom-state-search"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={selectAllVisible} data-testid="ecom-select-all-states">
                  Select All Visible
                </Button>
                <Button size="sm" variant="outline" onClick={clearAllVisible}>
                  Clear Visible
                </Button>
                <div className="text-sm text-gray-600">
                  <strong>{selectedCount}</strong> selected of {states.length}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[420px] overflow-y-auto p-1 border rounded-lg">
                {filteredStates().map((st) => {
                  const on = (settings.serving_states || []).includes(st);
                  return (
                    <label
                      key={st}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer border ${on ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                      data-testid={`ecom-state-item-${st}`}
                    >
                      <Checkbox
                        checked={on}
                        onCheckedChange={() => toggleState(st)}
                      />
                      <span className="text-sm text-gray-800">{st}</span>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TERMS */}
        <TabsContent value="terms" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Privacy Policy</CardTitle>
              <CardDescription>Displayed on the storefront's Privacy page.</CardDescription>
            </CardHeader>
            <CardContent>
              <ReactQuill
                theme="snow"
                modules={QUILL_TOOLBAR}
                value={settings.privacy_policy_html || ''}
                onChange={(html) => update({ privacy_policy_html: html })}
                data-testid="ecom-privacy-editor"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Terms of Use</CardTitle>
              <CardDescription>Displayed on the storefront's Terms page.</CardDescription>
            </CardHeader>
            <CardContent>
              <ReactQuill
                theme="snow"
                modules={QUILL_TOOLBAR}
                value={settings.terms_of_use_html || ''}
                onChange={(html) => update({ terms_of_use_html: html })}
                data-testid="ecom-terms-editor"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Return &amp; Cancellation Policy</CardTitle>
              <CardDescription>Displayed on the storefront's Return &amp; Cancellation page.</CardDescription>
            </CardHeader>
            <CardContent>
              <ReactQuill
                theme="snow"
                modules={QUILL_TOOLBAR}
                value={settings.return_cancellation_policy_html || ''}
                onChange={(html) => update({ return_cancellation_policy_html: html })}
                data-testid="ecom-return-editor"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* INTEGRATIONS */}
        <TabsContent value="integrations" className="mt-4 space-y-4">
          {/* Razorpay */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" /> Razorpay
              </CardTitle>
              <p className="text-xs text-gray-500">
                Get keys from Razorpay Dashboard → Settings → API Keys. Test-mode keys start with <code>rzp_test_</code>.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!settings.razorpay_enabled}
                  onChange={(e) => update({ razorpay_enabled: e.target.checked })}
                  data-testid="rzp-enabled"
                />
                Enable Razorpay checkout
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Key ID</Label>
                  <Input
                    value={settings.razorpay_key_id || ''}
                    onChange={(e) => update({ razorpay_key_id: e.target.value })}
                    placeholder="rzp_test_xxxxxxxxxxxxxx"
                    data-testid="rzp-key-id"
                  />
                </div>
                <div>
                  <Label>Key Secret</Label>
                  <Input
                    type="password"
                    value={settings.razorpay_key_secret || ''}
                    onChange={(e) => update({ razorpay_key_secret: e.target.value })}
                    placeholder="••••••••••••"
                    data-testid="rzp-key-secret"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Webhook Secret</Label>
                  <Input
                    type="password"
                    value={settings.razorpay_webhook_secret || ''}
                    onChange={(e) => update({ razorpay_webhook_secret: e.target.value })}
                    placeholder="Optional — set only after webhook is configured on Razorpay side"
                    data-testid="rzp-webhook-secret"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shiprocket */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-orange-600" /> Shiprocket
              </CardTitle>
              <p className="text-xs text-gray-500">
                Get credentials from Shiprocket Panel → Settings → API. Channel ID is the ecommerce store you set up inside Shiprocket.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!settings.shiprocket_enabled}
                  onChange={(e) => update({ shiprocket_enabled: e.target.checked })}
                  data-testid="sr-enabled"
                />
                Push orders to Shiprocket automatically after payment (live rate quotes only need the login below)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input
                    value={settings.shiprocket_email || ''}
                    onChange={(e) => update({ shiprocket_email: e.target.value })}
                    placeholder="you@company.com"
                    data-testid="sr-email"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={settings.shiprocket_password || ''}
                    onChange={(e) => update({ shiprocket_password: e.target.value })}
                    placeholder="••••••••"
                    data-testid="sr-password"
                  />
                </div>
                <div>
                  <Label>Channel ID</Label>
                  <Input
                    value={settings.shiprocket_channel_id || ''}
                    onChange={(e) => update({ shiprocket_channel_id: e.target.value })}
                    placeholder="e.g. 3128421"
                    data-testid="sr-channel"
                  />
                </div>
                <div>
                  <Label>Pickup Location</Label>
                  <Input
                    value={settings.shiprocket_pickup_location || 'Primary'}
                    onChange={(e) => update({ shiprocket_pickup_location: e.target.value })}
                    placeholder="Primary"
                    data-testid="sr-pickup"
                  />
                </div>
                <div>
                  <Label>Pickup Pincode</Label>
                  <Input
                    value={settings.pickup_pincode || ''}
                    onChange={(e) => update({ pickup_pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    placeholder="Store address pincode used if blank"
                    data-testid="sr-pickup-pincode"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Parcel origin for live shipping rate quotes (weight is taken from each product's Weight/Dimensions).</p>
                </div>
              </div>

              <ShippingRateTester storeId={selectedStoreId} disabled={!(settings.shiprocket_email && settings.shiprocket_password)} />

              <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 space-y-3" data-testid="sr-webhook-box">
                <div className="font-medium text-sm text-indigo-900">Shiprocket Webhook (automatic tracking updates)</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>Webhook URL (paste in Shiprocket → Settings → Webhooks)</Label>
                    <div className="flex gap-2">
                      <Input readOnly value={`${process.env.REACT_APP_BACKEND_URL}/api/ecom/webhooks/courier-updates/${selectedStoreId}`} className="font-mono text-xs bg-white" data-testid="sr-webhook-url" />
                      <Button type="button" variant="outline" onClick={() => { navigator.clipboard?.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/ecom/webhooks/courier-updates/${selectedStoreId}`); toast.success('URL copied'); }} data-testid="sr-webhook-copy-url">Copy</Button>
                    </div>
                  </div>
                  <div>
                    <Label>Token (Auth Token Type: x-api-key)</Label>
                    <div className="flex gap-2">
                      <Input value={settings.shiprocket_webhook_token || ''} onChange={(e) => update({ shiprocket_webhook_token: e.target.value })} className="font-mono text-xs bg-white" placeholder="Generate a token, Save, then paste in Shiprocket" data-testid="sr-webhook-token" />
                      <Button type="button" variant="outline" onClick={() => update({ shiprocket_webhook_token: Array.from(crypto.getRandomValues(new Uint8Array(24)), (b) => b.toString(16).padStart(2, '0')).join('') })} data-testid="sr-webhook-generate">Generate</Button>
                      <Button type="button" variant="outline" disabled={!settings.shiprocket_webhook_token} onClick={() => { navigator.clipboard?.writeText(settings.shiprocket_webhook_token); toast.success('Token copied'); }} data-testid="sr-webhook-copy-token">Copy</Button>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-indigo-900/70">Save settings first, then in Shiprocket enter the URL, choose Auth Token Type <b>x-api-key</b>, paste the token and click Test Webhook → Save. Pickup, in-transit, out-for-delivery, delivered and RTO updates will then land on orders automatically.</p>
              </div>
            </CardContent>
          </Card>

          {/* SMS OTP */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-emerald-600" /> SMS OTP
              </CardTitle>
              <p className="text-xs text-gray-500">
                Powers customer login OTP + order updates. Leave "Dev Mode" ON and the OTP is always <code>123456</code> — ideal for building the frontend before live SMS credentials are ready.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={settings.otp_dev_mode !== false}
                  onChange={(e) => update({ otp_dev_mode: e.target.checked })}
                  data-testid="otp-dev-mode"
                />
                Dev Mode (OTP always <b>123456</b>)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Provider</Label>
                  <select
                    value={settings.sms_provider || 'mock'}
                    onChange={(e) => update({ sms_provider: e.target.value })}
                    className="w-full h-10 rounded-md border px-3 text-sm"
                    data-testid="sms-provider"
                  >
                    <option value="mock">Mock (dev / logs only)</option>
                    <option value="meraotp">MeraOTP (recommended)</option>
                    <option value="msg91">MSG91</option>
                    <option value="fast2sms">Fast2SMS</option>
                    <option value="twilio">Twilio</option>
                    <option value="aws_sns">AWS SNS</option>
                  </select>
                </div>
                <div>
                  <Label>Sender ID</Label>
                  <Input
                    value={settings.sms_sender_id || ''}
                    onChange={(e) => update({ sms_sender_id: e.target.value })}
                    placeholder="e.g. SYSTOR"
                    data-testid="sms-sender-id"
                  />
                </div>
                <div>
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={settings.sms_api_key || ''}
                    onChange={(e) => update({ sms_api_key: e.target.value })}
                    placeholder="Provider API key"
                    data-testid="sms-api-key"
                  />
                </div>
                <div>
                  <Label>Template ID (DLT)</Label>
                  <Input
                    value={settings.sms_template_id || ''}
                    onChange={(e) => update({ sms_template_id: e.target.value })}
                    placeholder="For MSG91 / Fast2SMS DLT compliance"
                    data-testid="sms-template-id"
                  />
                </div>
              </div>

              {/* Test SMS panel — verify config end-to-end */}
              <div className="mt-3 p-3 border rounded-md bg-slate-50 space-y-3" data-testid="test-sms-panel">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <Send className="h-3.5 w-3.5" /> Test SMS delivery
                </div>
                <p className="text-xs text-muted-foreground -mt-1">
                  Send a real OTP through the currently-saved provider. Save your changes first if you just switched providers.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    className="flex-1"
                    placeholder="10-digit mobile — e.g. 9876543210"
                    value={testMobile}
                    onChange={(e) => setTestMobile(e.target.value)}
                    maxLength={13}
                    data-testid="test-sms-mobile"
                  />
                  <Button
                    onClick={sendTestSms}
                    disabled={testing || !testMobile || !selectedStoreId}
                    data-testid="test-sms-btn"
                  >
                    {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Send test OTP
                  </Button>
                </div>

                {testResult && (
                  <div
                    className={`p-3 rounded-md text-sm border ${
                      testResult.success
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                        : 'bg-rose-50 border-rose-200 text-rose-900'
                    }`}
                    data-testid="test-sms-result"
                  >
                    <div className="flex items-start gap-2">
                      {testResult.success
                        ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        : <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                      <div className="flex-1">
                        <div className="font-medium">
                          {testResult.success ? 'OTP dispatched' : 'Send failed'}
                          <span className="text-xs font-normal ml-2 opacity-70">
                            via {testResult.provider || '—'}
                          </span>
                        </div>
                        <div className="text-xs mt-0.5">{testResult.message}</div>

                        {testResult.message_id && (
                          <div className="text-xs mt-1 font-mono opacity-70">
                            message_id: {testResult.message_id}
                          </div>
                        )}

                        {testResult.hint && (
                          <div className="mt-2 p-2 rounded bg-white/60 border border-current/20">
                            <div className="text-xs font-medium">Next step</div>
                            <div className="text-xs mt-0.5">{testResult.hint}</div>
                            {testResult.outbound_ip && (
                              <button
                                type="button"
                                onClick={() => {
                                  navigator.clipboard.writeText(testResult.outbound_ip);
                                  toast.success('IP copied');
                                }}
                                className="mt-1 inline-flex items-center gap-1 text-xs font-mono underline hover:no-underline"
                                data-testid="copy-outbound-ip"
                              >
                                <Copy className="h-3 w-3" /> Copy {testResult.outbound_ip}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {settings.sms_provider === 'meraotp' && (
                <div className="mt-3 p-3 border rounded-md bg-emerald-50/40 space-y-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
                    <span className="inline-block h-2 w-2 rounded-full bg-emerald-600" />
                    MeraOTP configuration
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>MeraOTP API Key</Label>
                      <Input
                        type="password"
                        value={settings.meraotp_api_key || ''}
                        onChange={(e) => update({ meraotp_api_key: e.target.value })}
                        placeholder="mo_live_…"
                        data-testid="meraotp-api-key"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Get it from <a href="https://new.meraotp.in" target="_blank" rel="noreferrer" className="underline">meraotp.in</a> → Dashboard → API Keys
                      </p>
                    </div>
                    <div>
                      <Label>OTP Length</Label>
                      <select
                        value={settings.meraotp_length || 6}
                        onChange={(e) => update({ meraotp_length: parseInt(e.target.value, 10) })}
                        className="w-full h-10 rounded-md border px-3 text-sm"
                        data-testid="meraotp-length"
                      >
                        <option value={4}>4 digits</option>
                        <option value={5}>5 digits</option>
                        <option value={6}>6 digits (recommended)</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Uncheck <b>Dev Mode</b> above for MeraOTP to actually send SMS. In dev mode the OTP is still <code>123456</code> and no SMS is dispatched.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StoreEcommerceSettingsPage;
